import React, { useState, useEffect } from 'react';
import partnerService from '../services/partnerService';
import useStripeConnect from '../hooks/useStripeConnect';
import './PartnerForm.css';
import './PaymentPorterPage.css'; // pp-toggle-btn — the shared active/inactive toggle (STYLE.md)

// THE Stripe Connect panel. One implementation, both screens that show it.
//
// It existed twice, near-identically: /partners/:id (PartnerForm, superadmin managing a partner) and
// /partner-settings (PartnerSettings, a partner managing itself). The copies had already drifted in
// wording, and adding the "Continue without Stripe" waiver to one of them would have left the other — the
// one a superadmin actually uses to set up a partner — without the control. Anything added here now shows
// up on both.
//
// The panel owns its own status fetch, so a caller supplies the partner and where Stripe should return to.
//
//   partnerId  - the partner whose Stripe account this is
//   returnPath - path Stripe redirects back to after onboarding (e.g. `/partners/5`)
//   canWaive   - show the superadmin-only skip_stripe toggle. The lambda enforces this independently;
//                the prop decides visibility, never permission.
//   reloadKey  - change it to force a refetch (callers fetch lazily when their tab opens)
const StripeConnectPanel = ({ partnerId, returnPath, canWaive = false, reloadKey }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingSkip, setSavingSkip] = useState(false);

  const { handleConnect, loading: redirecting, error: connectError } = useStripeConnect(partnerId, returnPath);

  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await partnerService.getStripeConnect(partnerId);
        if (!cancelled) setStatus(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error?.message || 'Failed to load Stripe status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId, reloadKey]);

  // Optimistic: the toggle shows the new state immediately and reverts if the save fails, so it never sits
  // on the old value while the request is in flight.
  const handleToggleSkipStripe = async () => {
    const next = status?.skipStripe ? 'N' : 'Y';
    setSavingSkip(true);
    setError(null);
    setStatus(prev => ({ ...prev, skipStripe: next === 'Y' }));
    try {
      await partnerService.updatePartner(partnerId, { skip_stripe: next });
    } catch (err) {
      setStatus(prev => ({ ...prev, skipStripe: next !== 'Y' }));
      setError(err.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSavingSkip(false);
    }
  };

  return (
    <div className="stripe-connect-panel">
      <h3>Stripe Connect</h3>
      <p className="stripe-connect-description">
        Connect this partner to Stripe to enable payment processing and payouts.
      </p>

      {loading && <div className="stripe-connect-loading">Loading Stripe status...</div>}

      {(error || connectError) && (
        <div className="stripe-connect-error">{error || connectError}</div>
      )}

      {!loading && status && (
        <>
          {status.onboardingComplete ? (
            <div className="stripe-connect-status stripe-connect-active">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M6 10l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <div className="stripe-connect-status-title">Connected to Stripe</div>
                {status.email && <div className="stripe-connect-status-detail">Account: {status.email}</div>}
                <div className="stripe-connect-status-detail">ID: {status.stripeAccountId}</div>
                <div className="stripe-connect-status-detail">
                  Charges: {status.chargesEnabled ? 'Enabled' : 'Disabled'} &nbsp;|&nbsp;
                  Payouts: {status.payoutsEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>
          ) : status.connected ? (
            <div className="stripe-connect-status stripe-connect-pending">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6v4l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div>
                <div className="stripe-connect-status-title">Onboarding in progress</div>
                <div className="stripe-connect-status-detail">Stripe onboarding has not been completed yet.</div>
              </div>
            </div>
          ) : (
            <div className="stripe-connect-status stripe-connect-none">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div>
                <div className="stripe-connect-status-title">Not connected</div>
                <div className="stripe-connect-status-detail">This partner does not have a Stripe Connect account yet.</div>
              </div>
            </div>
          )}

          {!status.onboardingComplete && (
            <button
              type="button"
              className="btn-stripe-connect"
              onClick={handleConnect}
              disabled={redirecting}
            >
              {redirecting ? 'Redirecting...' : status.connected ? 'Continue Stripe Onboarding' : 'Connect with Stripe'}
            </button>
          )}

          {/* Waiving Stripe for a whole partner is a commercial decision, not a self-service one —
              superadmin only, and the lambda ignores the field for anyone else rather than trusting this
              to hide it. */}
          {canWaive && (
            <div className="stripe-skip-panel sa-only-panel">
              <div className="stripe-skip-text">
                <div className="stripe-connect-status-title">Continue without Stripe</div>
                <div className="stripe-connect-status-detail">
                  When on, this partner can create hotel chains, hotels, vaults, sync projects and payment
                  porters without connecting Stripe. Revenue share still requires a connected account and
                  stays unpaid until one exists.
                </div>
              </div>
              <button
                type="button"
                className={`pp-toggle-btn ${status.skipStripe ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                onClick={handleToggleSkipStripe}
                disabled={savingSkip}
                title={status.skipStripe ? 'Click to require Stripe' : 'Click to allow this partner to skip Stripe'}
              >
                <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                <span className="pp-toggle-label">
                  {savingSkip ? 'Saving…' : status.skipStripe ? 'Active' : 'Inactive'}
                </span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StripeConnectPanel;
